import React, { memo, useCallback, useEffect, useState } from "react";
import EmptyPage from "../../app/components/empty-page/EmptyPage";
import PaginationCustom from "../../app/components/pagination/PaginationCustom";
import HeaderNew from "../../app/components/redesign/Header/HeaderNew";
import SortingLine from "../../app/components/sorting-line/SortingLine";
import { COLORS } from "../../core/consts/redesign/commonRedesign";
import { Sort } from "../../core/consts/redesign/icons";
import managerService from "../../core/services/ManagerService";
import { ClientTypes } from "../../core/types/agentTypes";
import { OptionType } from "../../core/types/common";
import {
  AgentsList,
  clientAgentInfoType,
  clientsAgentsListType,
  clientsDataSortType,
  ClientsSortEnum,
  ClientTypeOption,
  ClientTypesEnum,
} from "../../core/types/managerApiTypes";
import RadioOptionsBlock from "../../UIcomponentsRedesign/RadioOptionsBlock/RadioOptionsBlock";
import SpinnerMain from "../../UIcomponentsRedesign/SpinnerMain";
import UserCard from "./components/user-card/UserCard";
import UsersFilter from "./components/users-filter/UsersFilter";
import { useStylesAdminUsers } from "./useStylesAdminUsers";

/* Redesign . All admin and managers, master users page. */

const AdminUsersPage: React.FC = () => {
  const classes = useStylesAdminUsers();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isInnerLoading, setIsInnerLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState({
    offset: 0,
    count: 0,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortType, setSortType] = useState<ClientsSortEnum>(
    ClientsSortEnum.createTimeDesc
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchClientType, setSearchClientType] = useState<string>("");
  const [searchAgent, setSearchAgent] = useState<string>("");
  const [searchManager, setSearchManager] = useState<string>("");
  const [registrationDate, setRegistrationDate] = useState<string>("");
  const [filters, setFilters] = useState({
    clientTypeList: [] as ClientTypeOption[],
    agentsList: [] as OptionType[],
    managersList: [] as OptionType[],
    statusList: [] as string[],
  });
  const [isAgents, setIsAgents] = useState<boolean>(false);
  const [isClients, setIsClients] = useState<boolean>(false);
  const [isClientsAgents, setIsClientsAgents] = useState<boolean>(false);
  const [isExistedOnPyrus, setIsExistedOnPyrus] = useState<boolean>(false);
  const [searchStatus, setSearchStatus] = useState<string>("");
  const [clientsData, setClientsData] = useState<clientsAgentsListType>();
  const [currentClients, setCurrentClients] = useState<clientAgentInfoType[]>();

  const totalClients = pagination.total;
  const clientsPerPage = 32;
  const clientsExist = clientsData?.clients?.length ?? 0;
  const formatAgentsAndManagers = (list: AgentsList[] | undefined) => {
    return (list || [])
      .filter((item) => item.userName && item.userName.trim() !== "")
      .map((item) => ({
        label: item.userName,
        value: item.userId,
      }));
  };

  const formatClientTypes = (clientTypes: string[]): ClientTypeOption[] => {
    return clientTypes.map((type) => ({
      label: ClientTypes[type as keyof typeof ClientTypes],
      value: type as ClientTypesEnum,
    }));
  };

  const getClientsRequest = useCallback(() => {
    setIsInnerLoading(true);
    let Q = {
      sorting: sortType,
      filter: searchQuery,
      clientType: searchClientType === "all" ? undefined : searchClientType,
      agentId: searchAgent,
      managerId: searchManager,
      registrationDate: registrationDate,
      status: searchStatus,
      onlyClient: isClients,
      onlyAgent: isAgents,
      onlyAgentsClient: isClientsAgents,
      existsInPyrus: isExistedOnPyrus,
      count: clientsPerPage,
      offset: (currentPage - 1) * clientsPerPage,
    };
    managerService
      .getClientsAgentsListService(Q)
      .then((res: clientsAgentsListType) => {
        setClientsData(res);
        setPagination(res.pagination || { offset: 0, count: 0, total: 0 });
        setCurrentClients(res.clients);
        setIsLoading(false);
        setFilters((prevFilters) => ({
          ...prevFilters,
          clientTypeList: formatClientTypes(res.clientTypes),
          agentsList: formatAgentsAndManagers(res.agents),
          managersList: formatAgentsAndManagers(res.managers),
          statusList: res.clientStatuses || [],
        }));
        setIsInnerLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setIsInnerLoading(false);
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
        setIsInnerLoading(false);
      });
  }, [
    sortType,
    searchQuery,
    searchClientType,
    searchAgent,
    searchManager,
    registrationDate,
    searchStatus,
    isClients,
    isAgents,
    isClientsAgents,
    isExistedOnPyrus,
    currentPage,
  ]);

  useEffect(() => {
    const delay = 500;
    const timerId = setTimeout(() => {
      getClientsRequest();
    }, delay);
    return () => clearTimeout(timerId);
  }, [getClientsRequest]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage]);

  const paginate = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleClearFilters = useCallback(() => {
    setCurrentPage(1);
    setSearchStatus("");
    setSearchAgent("");
    setSearchManager("");
    setSearchClientType("");
    setIsClients(false);
    setIsAgents(false);
    setIsExistedOnPyrus(false);
    setIsClientsAgents(false);
    getClientsRequest();
  }, [getClientsRequest]);

  const handleClearQuery = useCallback(() => {
    setCurrentPage(1);
    setSearchQuery("");
    getClientsRequest();
  }, [getClientsRequest]);

  return (
    <div>
      <HeaderNew title={"Пользователи"} />
      <div className={classes.container}>
        <div className={classes.content}>
          {isLoading ? (
            <SpinnerMain />
          ) : (
            <>
              <UsersFilter
                agentsList={filters.agentsList}
                managersList={filters.managersList}
                searchClientType={searchClientType}
                setSearchClientType={setSearchClientType}
                searchStatus={searchStatus}
                setSearchStatus={setSearchStatus}
                statusList={filters.statusList}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onClear={handleClearFilters}
                onClearQuery={handleClearQuery}
                isClients={isClients}
                setIsClients={setIsClients}
                isAgents={isAgents}
                isClientsAgents={isClientsAgents}
                setIsAgents={setIsAgents}
                setIsClientsAgents={setIsClientsAgents}
                isExistedOnPyrus={isExistedOnPyrus}
                setIsExistedOnPyrus={setIsExistedOnPyrus}
                setCurrentPage={setCurrentPage}
                setSearchManager={setSearchManager}
                setSearchAgent={setSearchAgent}
                registrationDate={registrationDate}
                setRegistrationDate={setRegistrationDate}
                clientTypeList={filters.clientTypeList}
              />
              <SortingLine
                isUsersPage
                items={clientsData}
                searchPeriod={sortType}
                totalItems={totalClients}
              >
                <RadioOptionsBlock
                  value={sortType.toString()}
                  setValue={(value) => setSortType(value as ClientsSortEnum)}
                  optionsObjects={clientsDataSortType}
                  buttonIcon={<Sort color={COLORS.brand_blue} />}
                />
              </SortingLine>
              {isInnerLoading ? (
                <SpinnerMain fixedPosition />
              ) : clientsExist > 0 ? (
                <>
                  {currentClients &&
                    currentClients.map(
                      (client: clientAgentInfoType, index: number) => (
                        <UserCard clientInfo={client} key={index} />
                      )
                    )}
                  <PaginationCustom
                    currentPage={currentPage}
                    itemsPerPage={clientsPerPage}
                    totalItems={totalClients}
                    onPageChange={paginate}
                  />
                </>
              ) : (
                <EmptyPage
                  titleColor={COLORS.gray2}
                  title="Пользователи не найдены"
                  content="Попробуйте изменить параметры фильтрации"
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(AdminUsersPage);
